"use client";
import { useState, useCallback, useEffect, useRef } from "react";
import { Play } from "lucide-react";
import FallbackImage from "@/app/components/elements/FallbackImage";

interface ProductCardImageProps {
  images: string[];
  name: string;
  isVideo?: boolean;
  videoLink?: string;
  priority?: boolean;
  /**
   * Yatay carousel içinde mi? true ise yatay sürükleme tamamen carousel'e aittir:
   * `swiper-no-swiping` uygulanmaz ve touch galerisi (swipe ile resim değiştirme)
   * devre dışı kalır. Hover ile resim değiştirme çalışmaya devam eder — mousemove
   * ile drag farklı jestler olduğu için carousel sürüklemesiyle çakışmaz.
   */
  inCarousel?: boolean;
}

const ProductCardImage = ({ images, name, isVideo, videoLink, priority = false, inCarousel = false }: ProductCardImageProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const rafRef = useRef<number | null>(null);
  const pendingIndexRef = useRef<number>(0);
  const containerWidthRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  /** Kaydırmanın ürettiği tek bir hayalet click'i yutmak için — bkz. handleTouchStart. */
  const blockNextClick = useRef(false);

  // ResizeObserver ile containerWidth'i mount'ta ve boyut değişiminde güncelle.
  // Böylece onMouseEnter'da offsetWidth okuyarak reflow yaratılmaz.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    containerWidthRef.current = el.offsetWidth;
    const observer = new ResizeObserver((entries) => {
      containerWidthRef.current = entries[0]?.contentRect.width ?? 0;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hasImages = images && images.length > 0;

  useEffect(() => {
    if (!hasImages || images.length <= 1) return;
    // currentImageIndex 0 = kullanıcı galeriyi henüz kullanmadı. Bu koşul
    // olmadan mount'ta her kart images[1]'i indiriyordu: masaüstünde 84 ekstra
    // istek, hepsi LCP penceresiyle yarışıyor. Kullanıcı ilk kez resim
    // değiştirdiği anda komşu ön yükleme normal şekilde devreye giriyor.
    if (currentImageIndex === 0) return;
    const next = currentImageIndex + 1;
    const prev = currentImageIndex - 1;
    const toPreload = [next, prev].filter((i) => i >= 0 && i < images.length);
    const imgs = toPreload.map((i) => {
      const img = new Image();
      img.src = images[i];
      return img;
    });
    return () => { imgs.forEach((img) => { img.src = ""; }); };
  }, [currentImageIndex, images, hasImages]);

  /**
   * Konuma göre resim değiştirme YALNIZCA gerçek fare içindir.
   *
   * Mouse olayları bunu ayırt edemiyordu: mobilde bir dokunuş bittiğinde
   * tarayıcı uyumluluk için sentetik mouse olayları üretiyor
   * (mouseover → mouseenter → mousemove → click). Eski kodda mouseenter
   * "dokunma değil" bayrağını sıfırlıyor, hemen ardından gelen sentetik
   * mousemove de parmağın bastığı x konumuna göre fotoğrafı değiştiriyordu —
   * kullanıcı sadece tıklamışken kart sağa/sola atlıyordu.
   *
   * Pointer olaylarında bu belirsizlik yok: dokunma `pointerType: "touch"`
   * gelir ve tarayıcı uyumluluk katmanında pointer olayı ÜRETMEZ (yalnızca
   * mouse olayları üretir). Dolayısıyla pointerType kontrolü, dokunmayla gelen
   * her şeyi kesin olarak dışarıda bırakır.
   */
  const handleImageHover = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      if (!hasImages || images.length <= 1) return;
      // Basılı tutup sürüklerken (carousel drag) resim değiştirme — aksi halde
      // kullanıcı carousel'i çekerken kartın fotoğrafları titrer.
      if (e.buttons !== 0) return;
      const width = containerWidthRef.current;
      if (!width) return;
      const x = e.nativeEvent.offsetX;
      const newIndex = Math.min(Math.floor((x / width) * images.length), images.length - 1);
      pendingIndexRef.current = newIndex;
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setCurrentImageIndex(pendingIndexRef.current);
      });
    },
    [images, hasImages],
  );

  // Aynı gerekçeyle pointer: dokunmayla kaydırıp başka yere basıldığında gelen
  // sentetik mouseleave, mobilde seçili fotoğrafı 1. kareye geri atıyordu.
  const handlePointerLeave = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setCurrentImageIndex(0);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    // YENİ BİR DOKUNUŞ = temiz sayfa. Bayrağın ömrü bir önceki jestle sınırlı;
    // burada koşulsuz sıfırlandığı için hiçbir zaman GERÇEK bir tıklamayı
    // yiyemez: gerçek tıklama daima kendi touchstart'ından sonra gelir, o da
    // bayrağı bu satırda düşürmüş olur. Kartın kalıcı olarak tıklanamaz hale
    // gelmesi (kaydır → tıkla → hiçbir şey olmaz) bu yüzden imkânsız.
    blockNextClick.current = false;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!hasImages || images.length <= 1) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    const isHorizontalSwipe = Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY) * 2;
    if (!isHorizontalSwipe) return;
    // Kaydırma jesti bittiğinde tarayıcı ardından bir "hayalet" click üretir
    // (Android Chrome her zaman; iOS Safari touch-action yüzünden yatay hareketi
    // sayfa kaydırması saymadığı için o da üretir). Yalnızca O click yutulacak.
    blockNextClick.current = true;
    if (deltaX < 0) {
      setCurrentImageIndex((prev) => Math.min(prev + 1, images.length - 1));
    } else {
      setCurrentImageIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  // Yutma capture fazında ve React'in kendi olay sisteminde yapılıyor:
  // stopPropagation üstteki <Link>'in onClick'ine (trackProductClick + onNavigate)
  // ulaşmayı, preventDefault ise <a>'nın gezinmesini engelliyor. Native listener
  // eklenmediği için temizlenecek bir şey de yok.
  const handleClickCapture = (e: React.MouseEvent) => {
    if (!blockNextClick.current) return;
    blockNextClick.current = false; // tek seferlik
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full bg-linear-to-br from-gray-50 to-gray-100 group/image cursor-pointer${hasImages && images.length > 1 && !inCarousel ? " swiper-no-swiping" : ""}`}
      onPointerMove={handleImageHover}
      onPointerLeave={handlePointerLeave}
      onTouchStart={inCarousel ? undefined : handleTouchStart}
      onTouchEnd={inCarousel ? undefined : handleTouchEnd}
      onClickCapture={inCarousel ? undefined : handleClickCapture}
      // touch-action: pan-y — yalnızca kendi galerimizi çalıştırdığımızda.
      //
      // Tarayıcıya "dikey kaydırma senin, YATAY hareket benim" diyor. Bu
      // olmadan iOS Safari yatay jesti kendi sayfa hareketi sanıp jesti iptal
      // edebiliyor: touchend yerine touchcancel üretiyor, handleTouchEnd hiç
      // çalışmıyor ve ardından gelen click öngörülemez hale geliyor. Android
      // bu konuda daha toleranslı olduğu için sorun yalnızca iOS'ta görünüyordu.
      style={
        !inCarousel && hasImages && images.length > 1
          ? { touchAction: "pan-y" }
          : undefined
      }
    >
      {hasImages ? (
        <>
          {images.map((image, index) => {
            const isActive = currentImageIndex === index;
            const isAdjacent = Math.abs(currentImageIndex - index) === 1;
            if (!isActive && !isAdjacent && index !== 0) return null;

            return (
              <div
                key={index}
                className="absolute inset-0"
                style={{
                  opacity: isActive ? 1 : 0,
                  transition: "opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                  pointerEvents: isActive ? "auto" : "none",
                }}
              >
                <FallbackImage
                  src={image}
                  alt={`${name} - ${index + 1}`}
                  fill
                  className="object-cover group-hover/card:will-change-transform group-hover/card:scale-[1.02] transition-transform duration-200 ease-out"
                  fallbackClassName="object-contain opacity-60 p-12"
                  sizes="(max-width: 480px) 45vw, (max-width: 640px) 32vw, (max-width: 768px) 24vw, (max-width: 1024px) 20vw, (max-width: 1280px) 17vw, 15vw"
                  quality={70}
                  // LCP kart (priority=true, index=0).
                  //
                  // Next 16'da `priority` yalnızca <head>'e preload link'i
                  // ekliyor ve loading'i eager'a çeviriyor — img'e
                  // fetchpriority YAZMIYOR (bkz. get-img-props: fetchPriority
                  // ayrı bir prop olarak geçiyor). Lighthouse'un "LCP
                  // kaynağına fetchpriority=high uygulanmalıdır" maddesi bu
                  // yüzden açıkta kalıyordu; attribute burada elle veriliyor.
                  // Diğer kartlar: loading="lazy".
                  priority={priority && index === 0}
                  fetchPriority={priority && index === 0 ? "high" : undefined}
                  loading={priority && index === 0 ? undefined : "lazy"}
                />
              </div>
            );
          })}
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="relative w-3/5 h-3/5">
            <FallbackImage
              src=""
              alt={name}
              fill
              fallbackClassName="object-contain opacity-60"
              sizes="(max-width: 480px) 45vw, (max-width: 640px) 32vw, (max-width: 768px) 24vw, (max-width: 1024px) 20vw, (max-width: 1280px) 17vw, 15vw"
            />
          </div>
        </div>
      )}
      {hasImages && images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-20">
          {images.map((_: string, index: number) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                currentImageIndex === index ? "bg-main scale-125" : "bg-[#4e4e4e]"
              }`}
            />
          ))}
        </div>
      )}
      {isVideo && videoLink && (
        <div className="absolute top-2 left-2 z-20">
          <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Play size={12} className="fill-white" />
            <span>Video</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCardImage;